import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useHelpDeskTickets } from "../../context/HelpDeskTicketsContext";
import {
  HelpDeskResponseMethod,
  HelpDeskResponseMethodTranslation,
  HelpDeskTicketStatus,
  HelpDeskTicketStatusBadge,
  HelpDeskTicketWithDetails,
  UpdateHelpDeskTicket,
} from "../../types/help_desk_tickets.types";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { Button } from "../../components/button";
import { HelpDeskTicketsAPI } from "../../APIs/help_desk_tickets.api";
import { ErrorMessage } from "../../components/error-message";
import { useToast } from "../../components/toast";
import { formatPhoneNumber } from "../../utils/format-phone-number.util";
import { EditableField } from "../../components/editable-field";
import Label from "../../components/label";
import { Loading } from "../../components/loading";

export const HelpDeskView = () => {
  const {
    updateHelpDeskTicket,
    isLoading,
    error: hpTicketError,
  } = useHelpDeskTickets();

  const { id } = useParams<{ id: string }>();
  const [error, setError] = useState<string | null>(null);
  const [helpDeskTicket, setHelpDeskTicket] =
    useState<HelpDeskTicketWithDetails | null>(null);
  const [isLoadingLocal, setIsLoading] = useState(true);

  const [formData, setFormData] = useState<UpdateHelpDeskTicket | null>(null);
  const { addToast } = useToast();

  const getHelpDeskTicket = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const response = await HelpDeskTicketsAPI.getHelpDeskTicket(id);
      if (response.statusCode === 200) {
        setHelpDeskTicket(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError("Error al obtener la solicitud de ayuda");
    } finally {
      setIsLoading(false);
    }
  }, []);
  const ticketStatus = useMemo(() => helpDeskTicket?.status, [helpDeskTicket]);

  useEffect(() => {
    if (!id) return;
    const ticketId = parseInt(id, 10);
    if (isNaN(ticketId)) {
      setError("ID de ticket inválido");
      return;
    }
    getHelpDeskTicket(ticketId);
    if (ticketStatus === HelpDeskTicketStatus.Pending) {
      setFormData({
        response: "",
        admin_comment: "",
        response_method: undefined,
        status: HelpDeskTicketStatus.Pending,
      });
    }
  }, [id, getHelpDeskTicket, ticketStatus]);

  const handleStatusUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!helpDeskTicket || !id) return;
    if (!formData) return;

    if (
      !formData.response_method === null ||
      formData.response_method === undefined
    ) {
      addToast({
        message: "Debe seleccionar una forma de respuesta",
        type: "error",
        location: "bottom-center",
      });
      return;
    }

    formData.status =
      formData.status === HelpDeskTicketStatus.Resolved
        ? HelpDeskTicketStatus.Pending
        : HelpDeskTicketStatus.Resolved;

    updateHelpDeskTicket(parseInt(id, 10), formData);

    if (!hpTicketError) {
      await getHelpDeskTicket(parseInt(id, 10));
    }
  };
  if (error || hpTicketError) {
    return (
      <div>
        <BackButton />
        <ErrorMessage
          message={
            error || hpTicketError || "Error al obtener la solicitud de ayuda"
          }
        />
      </div>
    );
  }
  if (isLoading || isLoadingLocal) {
    return <Loading />;
  }
  if (!helpDeskTicket) {
    return null;
  }

  const formatDate = (date: Date | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLTextAreaElement | HTMLSelectElement;

    setFormData((prev) => {
      if (!prev) return null;

      return {
        ...prev,
        [name]: value as HelpDeskTicketStatus | HelpDeskResponseMethod | string,
      };
    });
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="min-h-screen p-8">
      <BackButton />

      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {helpDeskTicket.title}
            </h1>
            <p className="text-sm text-gray-500 mt-1 flex items-center">
              <span className="mr-2">N°: #{helpDeskTicket.id}</span>
              <span className="mx-2">•</span>
              <CalendarIcon className="w-4 h-4 mr-1" />
              {formatDate(helpDeskTicket.created_at)}
            </p>
          </div>

          <Label
            color={
              HelpDeskTicketStatusBadge[helpDeskTicket.status].color as any
            }
            fontSize="sm"
          >
            {HelpDeskTicketStatusBadge[helpDeskTicket.status].label}
          </Label>
        </div>

        {helpDeskTicket.status === HelpDeskTicketStatus.Pending && (
          <form onSubmit={handleStatusUpdate}>
            <div className="border-t pt-4">
              <input
                type="hidden"
                name="status"
                value={helpDeskTicket.status}
              />

              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Actualizar Estado
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EditableField
                    label="Respuesta al usuario"
                    id="response"
                    type="textarea"
                    name="response"
                    placeholder="Escriba la respuesta para el usuario..."
                    value={formData?.response}
                    onChange={handleChange}
                    rows={3}
                  />
                  <EditableField
                    label="Comentario Interno"
                    id="admin_comment"
                    type="textarea"
                    name="admin_comment"
                    placeholder="Escriba el comentario interno..."
                    rows={3}
                    value={formData?.admin_comment}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col md:flex-row md:items-end space-y-4 md:space-y-0 md:space-x-4">
                  <EditableField
                    label="Forma de Respuesta"
                    id="response_method"
                    name="response_method"
                    type="select"
                    value={formData?.status}
                    placeholder="Seleccione una forma de respuesta"
                    onChange={handleChange}
                    options={Object.entries(
                      HelpDeskResponseMethodTranslation
                    ).map(([key, value]) => ({
                      key,
                      value,
                    }))}
                    required
                  />

                  {ticketStatus === HelpDeskTicketStatus.Pending && (
                    <Button
                      type="submit"
                      disabled={isLoading}
                      variant="create"
                      text="Resolver Ticket"
                      icon={<CheckCircleIcon className="w-5 h-5 mr-2" />}
                    />
                  )}
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Descripción</h2>
            <p className="text-gray-700 whitespace-pre-line">
              {helpDeskTicket.description || "No se proporcionó descripción."}
            </p>
          </div>

          {helpDeskTicket.response_method && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Respuesta</h2>
              <p className="text-gray-700 whitespace-pre-line">
                {helpDeskTicket.response || "-"}
              </p>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center text-sm text-gray-900">
                  <span className="mr-2">Forma de respuesta:</span>
                  <div className="flex items-center">
                    {
                      HelpDeskResponseMethodTranslation[
                        helpDeskTicket.response_method as HelpDeskResponseMethod
                      ]
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {helpDeskTicket.admin_comment && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Comentario Interno</h2>
              <p className="text-gray-700 whitespace-pre-line">
                {helpDeskTicket.admin_comment}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">
              Información de Contacto
            </h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <UserIcon className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">ID de Usuario</p>
                  <p className="font-medium">
                    {" "}
                    <a
                      href={`/dashboard/users/view/${helpDeskTicket.user_id}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {helpDeskTicket.user_id}
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <UserIcon className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Nombre</p>
                  <p className="font-medium">{helpDeskTicket.name || "-"}</p>
                </div>
              </div>
              <div className="flex items-start">
                <EnvelopeIcon className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Correo</p>
                  <p className="font-medium">{helpDeskTicket.email || "-"}</p>
                </div>
              </div>
              <div className="flex items-start">
                <PhoneIcon className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <p className="font-medium">
                    {formatPhoneNumber(helpDeskTicket.phone_number) || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {ticketStatus === HelpDeskTicketStatus.Resolved && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">
                Administrador de la Solicitud
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <UserIcon className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">ID de Usuario</p>
                    <p className="font-medium">{helpDeskTicket.responded_by}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start">
                  <UserIcon className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Nombre de Usuario</p>
                    <p className="font-medium">
                      {helpDeskTicket.responded_by_username}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Cronología</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                <div>
                  <p className="font-medium">Creado</p>
                  <p className="text-sm text-gray-500">
                    {formatDate(helpDeskTicket.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3"></div>
                <div>
                  <p className="font-medium">Última Actualización</p>
                  <p className="text-sm text-gray-500">
                    {formatDate(helpDeskTicket.updated_at)}
                  </p>
                </div>
              </div>
              {helpDeskTicket.closed_at && (
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                  <div>
                    <p className="font-medium">Cerrado</p>
                    <p className="text-sm text-gray-500">
                      {formatDate(helpDeskTicket.closed_at)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BackButton = () => {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      text="Volver a solicitudes"
      className="text-gray-700 hover:text-gray-900 transition-colors items-center rounded-lg mb-4"
      icon={<ArrowLeftIcon className="w-5 h-5 mr-2" />}
      onClick={() => navigate(-1)}
    />
  );
};
